// AI-mode helpers for the activity composer: suggestions land in the real fields,
// glow violet until the AM accepts (✓) or rejects (✕) each one. Nothing saves while any are pending.
import { useState } from "react";
import { toast } from "sonner";
import { Check, X, Sparkles, Loader2, CheckCheck } from "lucide-react";
import api from "../../lib/api";
import { TYPE_FIELDS } from "./activityMeta";
import { useTheme } from "../../context/ThemeContext";

export const AI_RING = "ring-2 ring-violet-400/70 ring-offset-1 ring-offset-transparent rounded-lg bg-violet-500/[0.07]";
const SCHEMA = Object.fromEntries(Object.entries(TYPE_FIELDS).map(([t, r]) => [t, (r.fields || []).map(({ key, kind, options, label }) => ({ key, kind, options, label }))]));

export function AiTag({ k, onAccept, onReject }) {
  const slug = k.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  return (
    <span className="inline-flex items-center gap-1 ml-2 align-middle normal-case tracking-normal font-sans" data-testid={`ai-tag-${slug}`}>
      <span className="px-1.5 py-[1px] rounded bg-violet-500/25 text-violet-200 text-[9.5px] font-bold">AI suggestion</span>
      <button type="button" onClick={() => onAccept(k)} title="Accept" data-testid={`ai-accept-${slug}`} className="w-5 h-5 grid place-items-center rounded bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/35"><Check className="w-3 h-3" /></button>
      <button type="button" onClick={() => onReject(k)} title="Reject" data-testid={`ai-reject-${slug}`} className="w-5 h-5 grid place-items-center rounded bg-rose-500/20 text-rose-300 hover:bg-rose-500/35"><X className="w-3 h-3" /></button>
    </span>
  );
}

export function AiBar({ count, onAcceptAll, onRejectAll }) {
  const light = useTheme().theme === "light";
  if (!count) return null;
  return (
    <div className="sticky top-[57px] z-[5] flex items-center gap-2 rounded-lg border border-violet-400/40 px-3 py-2 text-[12px]" data-testid="ai-suggest-bar"
      style={{ backgroundColor: light ? "#F3EEFF" : "#1E1838", color: light ? "#4C1D95" : "#EDE9FE" }}>
      <Sparkles className="w-4 h-4 text-violet-300 shrink-0" />
      <span className="flex-1"><b data-testid="ai-pending-count">{count}</b> AI suggestion{count === 1 ? "" : "s"} (violet) — check each one and make it yours. Nothing saves until you accept or reject them.</span>
      <button type="button" onClick={onRejectAll} className="px-2 py-1 rounded text-rose-200 hover:bg-rose-500/20" data-testid="ai-reject-all">Reject all</button>
      <button type="button" onClick={onAcceptAll} className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-violet-600 hover:bg-violet-500 font-semibold" data-testid="ai-accept-all"><CheckCheck className="w-3.5 h-3.5" /> Accept all</button>
    </div>
  );
}

export function AiFillButton({ busy, onClick, disabled }) {
  return (
    <button type="button" onClick={onClick} disabled={busy || disabled} data-testid="activity-ai-fill"
      title="AI reads the description and suggests the type, details, title, status and follow-up"
      className="inline-flex items-center gap-1 px-2.5 h-[30px] rounded-lg border border-violet-400/40 text-violet-200 text-[11px] font-bold hover:bg-violet-500/15 disabled:opacity-40">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {busy ? "Thinking…" : "AI fill"}
    </button>
  );
}

// state = { form, details, follow, statusTouched } ; set = { setForm, setDetails, setFollow, setStatusTouched }
export function useAiSuggest(state, set, ctx) {
  const [pending, setPending] = useState({});
  const [busy, setBusy] = useState(false);
  const [used, setUsed] = useState(false);
  const drop = (keys) => setPending((p) => { const n = { ...p }; keys.forEach((k) => delete n[k]); return n; });

  const suggest = async (text) => {
    if (!text.trim()) { toast.error("Say or type what happened first"); return; }
    setBusy(true);
    try {
      const { data: s } = await api.post("/ai/activity-suggest", { text, schema: SCHEMA, activity_type: state.form.activity_type, customer: ctx.customer, quote_ref: ctx.quoteRef }, { timeout: 90000 });
      apply(s);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI suggestions failed"); }
    finally { setBusy(false); }
  };

  // Same AI as the old top-bar "AI activity": drafts from the quote's CW staff notes, lands as violet suggestions.
  const fromQuoteNotes = async () => {
    if (!ctx.quoteId) return;
    setBusy(true);
    try {
      const { data: s } = await api.post(`/quotes/${ctx.quoteId}/activity-draft`, { text: state.form.content || "" }, { timeout: 90000 });
      apply({ ...s, details: {} });
    } catch (e) { toast.error(e?.response?.data?.detail || "AI draft failed"); }
    finally { setBusy(false); }
  };

  const apply = (s) => {
    {
      const { form, details, follow, statusTouched } = state;
      const pend = {}; const f = {}; let det = details;
      if (s.activity_type && s.activity_type !== form.activity_type) {
        pend.type = { activity_type: form.activity_type, details };
        f.activity_type = s.activity_type; det = ctx.prefill(s.activity_type);
      }
      const nd = { ...det };
      Object.entries(s.details || {}).forEach(([k, v]) => { if (nd[k] !== v) { pend[`d:${k}`] = det[k]; nd[k] = v; } });
      ["subject", "content", "priority", "due_date"].forEach((k) => { if (s[k] && s[k] !== form[k]) { pend[k] = form[k]; f[k] = s[k]; } });
      if (s.status && s.status !== form.status) { pend.status = { status: form.status, touched: statusTouched }; f.status = s.status; set.setStatusTouched(true); }
      if (s.follow_up_date) { pend.follow = follow; set.setFollow({ on: true, due: s.follow_up_date, touched: true }); }
      set.setForm((x) => ({ ...x, ...f }));
      set.setDetails(nd);
      setPending((p) => ({ ...pend, ...p }));
      setUsed(true);
      const n = Object.keys(pend).length;
      toast[n ? "success" : "message"](n ? `${n} suggestion${n === 1 ? "" : "s"} added — accept or reject each` : "Nothing new to suggest");
    }
  };

  const reject = (k) => {
    const prev = pending[k];
    if (k === "type") {
      set.setForm((x) => ({ ...x, activity_type: prev.activity_type })); set.setDetails(prev.details);
      drop(Object.keys(pending).filter((x) => x === "type" || x.startsWith("d:"))); return;
    }
    if (k.startsWith("d:")) set.setDetails((d) => ({ ...d, [k.slice(2)]: prev }));
    else if (k === "status") { set.setForm((x) => ({ ...x, status: prev.status })); set.setStatusTouched(prev.touched); }
    else if (k === "follow") set.setFollow(prev);
    else set.setForm((x) => ({ ...x, [k]: prev ?? "" }));
    drop([k]);
  };
  const accept = (k) => drop([k]);
  const rejectAll = () => {
    if (pending.type) reject("type");
    Object.keys(pending).filter((k) => k !== "type" && !k.startsWith("d:")).forEach(reject);
    if (!pending.type) Object.keys(pending).filter((k) => k.startsWith("d:")).forEach(reject);
    setPending({});
  };
  const markUsed = () => setUsed(true);
  return { pending, busy, used, suggest, fromQuoteNotes, accept, reject, acceptAll: () => setPending({}), rejectAll, markUsed, has: (k) => k in pending };
}
